import React, { useState } from 'react';
import './AccordionGallery.css';

export interface AccordionGalleryItem {
  image: string;
  label?: string;
  link?: string;
  alt?: string;
}

export interface AccordionGalleryProps {
  items?: AccordionGalleryItem[];
  defaultIndex?: number;
  accentColor?: string;
  overlayColor?: string;
  textColor?: string;
  height?: number;
  gap?: number;
  radius?: number;
  expandRatio?: number;
  orientation?: 'horizontal' | 'vertical';
  duration?: number;
  ease?: string;
  parallax?: number;
  tilt?: number;
  stagger?: number;
  trigger?: 'hover' | 'click';
  showLabels?: boolean;
  grayscale?: boolean;
  className?: string;
  [key: string]: any;
}

const DEFAULT_ITEMS: AccordionGalleryItem[] = [
  { image: 'https://picsum.photos/id/1015/900/1200', label: 'Canyon', link: '#' },
  { image: 'https://picsum.photos/id/1018/900/1200', label: 'Ridgeline', link: '#' },
  { image: 'https://picsum.photos/id/1039/900/1200', label: 'Falls', link: '#' },
  { image: 'https://picsum.photos/id/1043/900/1200', label: 'Harbour', link: '#' },
  { image: 'https://picsum.photos/id/1044/900/1200', label: 'Skyline', link: '#' }
];

export const AccordionGallery: React.FC<AccordionGalleryProps> = ({
  items = DEFAULT_ITEMS,
  defaultIndex = 0,
  accentColor = '#C1348B',
  overlayColor = '#060010',
  textColor = '#ffffff',
  height = 460,
  gap = 12,
  radius = 18,
  expandRatio = 0.46,
  orientation = 'horizontal',
  duration = 0.4,
  trigger = 'hover',
  showLabels = true,
  grayscale = false,
  className = ''
}) => {
  const count = items.length;
  const [active, setActive] = useState(Math.min(Math.max(defaultIndex, 0), count - 1));
  const vertical = orientation === 'vertical';

  const handleEnter = (i: number) => {
    if (trigger === 'hover') setActive(i);
  };

  const handleClick = (i: number, e: React.MouseEvent) => {
    if (i !== active) {
      e.preventDefault();
      setActive(i);
    }
  };

  const handleKeyDown = (i: number, e: React.KeyboardEvent) => {
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i + 1) % count);
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i - 1 + count) % count);
    }
  };

  // Cálculo del ratio de expansión para el flex dinámico
  const activeFlex = count > 1 ? (expandRatio * (count - 1)) / (1 - expandRatio) : 1;

  return (
    <div
      className={`accordion-gallery${vertical ? ' accordion-gallery--vertical' : ''}${className ? ` ${className}` : ''}`}
      style={{
        '--ag-accent': accentColor,
        '--ag-overlay': overlayColor,
        '--ag-text': textColor,
        '--ag-gap': `${gap}px`,
        '--ag-radius': `${radius}px`,
        height: vertical ? `${Math.round(height * 1.5)}px` : `${height}px`
      } as React.CSSProperties}
      role="list"
      aria-label="Galería interactiva de persiana"
    >
      {items.map((item, i) => {
        const isActive = i === active;
        const Tag = item.link && item.link !== '#' ? 'a' : 'div';
        return (
          <Tag
            key={i}
            className={`ag-panel${isActive ? ' ag-panel--active' : ''}`}
            style={{
              borderRadius: `${radius}px`,
              flex: isActive ? `${activeFlex.toFixed(2)} 1 0%` : '1 1 0%',
              transition: `flex ${duration}s cubic-bezier(0.25, 1, 0.5, 1)`
            }}
            href={item.link && item.link !== '#' ? item.link : undefined}
            onClick={(e: React.MouseEvent) => handleClick(i, e)}
            onMouseEnter={() => handleEnter(i)}
            onFocus={() => setActive(i)}
            onKeyDown={(e: React.KeyboardEvent) => handleKeyDown(i, e)}
            role="listitem"
            tabIndex={0}
            aria-current={isActive ? 'true' : undefined}
            aria-label={item.label}
          >
            <div className="ag-panel__frame">
              <div className="ag-panel__media">
                <img
                  src={item.image}
                  alt={item.alt || item.label || ''}
                  draggable="false"
                  loading="lazy"
                  decoding="async"
                  style={{
                    filter: grayscale
                      ? (isActive ? 'grayscale(0%) brightness(1)' : 'grayscale(40%) brightness(0.72)')
                      : (isActive ? 'brightness(1)' : 'brightness(0.85)')
                  }}
                />
              </div>
              <div
                className="ag-panel__overlay"
                aria-hidden="true"
                style={{
                  background: isActive
                    ? `linear-gradient(180deg, transparent 40%, ${overlayColor}cc 100%)`
                    : `linear-gradient(180deg, rgba(6,0,16,0.15) 0%, ${overlayColor}99 100%)`
                }}
              />
            </div>

            {showLabels && item.label && (
              <div className={`ag-panel__label${isActive ? ' ag-panel__label--visible' : ''}`} aria-hidden="true">
                <span
                  className="ag-panel__bar"
                  style={{ background: `linear-gradient(180deg, #c1348b, ${accentColor})` }}
                />
                <span className="ag-panel__text" style={{ color: textColor }}>
                  {item.label}
                </span>
              </div>
            )}
          </Tag>
        );
      })}
    </div>
  );
};

export default AccordionGallery;
